import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchTrainings, imageUrl } from '../../api/client';
import './Training.css';

const FILTERS = ['All', 'Training', 'Workshop'];

function IconClock() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.4" />
      <path d="M8 4.5V8l3 1.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}
function IconPin() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
      <path d="M8 1.6a5 5 0 015 5c0 3.4-5 7.8-5 7.8s-5-4.4-5-7.8a5 5 0 015-5z" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="8" cy="6.6" r="1.7" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  );
}
function IconArrow() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
      <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function IconImage() {
  return (
    <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
      <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="8.5" cy="9.5" r="1.6" stroke="currentColor" strokeWidth="1.4" />
      <path d="M21 15l-5-5-9 9" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
  );
}

export default function Training() {
  const navigate = useNavigate();
  const [items, setItems]   = useState([]);
  const [status, setStatus] = useState('loading'); // loading | ok | error
  const [filter, setFilter] = useState('All');

  useEffect(() => {
    let alive = true;
    fetchTrainings()
      .then((data) => { if (alive) { setItems(data); setStatus('ok'); } })
      .catch(() => { if (alive) setStatus('error'); });
    return () => { alive = false; };
  }, []);

  const visible = useMemo(
    () => (filter === 'All' ? items : items.filter((i) => i.type === filter)),
    [items, filter]
  );

  return (
    <div className="trp">

      {/* ── Hero ── */}
      <section className="trp-hero">
        <div className="trp-hero__blob" aria-hidden="true" />
        <span className="trp-hero__eyebrow">Training &amp; Workshops</span>
        <h1 className="trp-hero__title">Learn. Build. Grow With Us</h1>
        <div className="trp-hero__rule" />
        <p className="trp-hero__sub">
          SkillSync Pro offers hands-on training programs and workshops designed to
          build real, job-ready skills — led by our team of practicing developers and designers.
        </p>
      </section>

      {/* ── Filters ── */}
      <div className="trp-tabs">
        {FILTERS.map((f) => (
          <button
            key={f}
            className={`trp-tab ${filter === f ? 'trp-tab--active' : ''}`}
            onClick={() => setFilter(f)}
          >
            {f === 'All' ? 'All Programs' : `${f}s`}
          </button>
        ))}
      </div>

      {/* ── Content ── */}
      {status === 'loading' && (
        <p className="trp-status">Loading training programs…</p>
      )}

      {status === 'error' && (
        <p className="trp-status">
          Couldn&rsquo;t load training programs right now. Please check back shortly,
          or reach out via our <a href="/contact" style={{ color: '#f31616' }}>contact page</a>.
        </p>
      )}

      {status === 'ok' && visible.length === 0 && (
        <p className="trp-status">
          {items.length === 0
            ? 'New training programs and workshops are coming soon — check back shortly!'
            : 'Nothing in this category yet.'}
        </p>
      )}

      {status === 'ok' && visible.length > 0 && (
        <div className="trp-grid">
          {visible.map((item) => (
            <div className="trp-card" key={item.id}>
              <div className="trp-card__img-wrap">
                {item.image_url ? (
                  <img className="trp-card__img" src={imageUrl(item.image_url)} alt={item.title} loading="lazy" />
                ) : (
                  <div className="trp-card__img-fallback"><IconImage /></div>
                )}
                <span className="trp-card__badge">{item.type}</span>
              </div>
              <div className="trp-card__body">
                <h3 className="trp-card__title">{item.title}</h3>
                {item.description && <p className="trp-card__desc">{item.description}</p>}
                <div className="trp-card__meta">
                  {item.duration && <span><IconClock /> {item.duration}</span>}
                  {item.mode && <span><IconPin /> {item.mode}</span>}
                </div>
                <button className="trp-card__cta" onClick={() => navigate('/contact')}>
                  Enquire Now <IconArrow />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── CTA band ── */}
      <section className="trp-cta-band">
        <h2 className="trp-cta-band__title">Want to run a custom workshop for your team?</h2>
        <p className="trp-cta-band__sub">We design corporate and campus training around your goals.</p>
        <a className="trp-cta-band__btn" href="/contact">
          Get in Touch <IconArrow />
        </a>
      </section>
    </div>
  );
}
